import { useCallback, useEffect, useRef } from "react"
import api from "../../../services/api"
import { canais } from "../../../utils/canais"

/**
 * Hook responsável por marcar mensagens/tickets como lidos via HTTP (REST).
 *
 * Desacoplado do Socket.io para garantir entrega confiável independente de
 * reconexões ou instabilidades de rede.
 *
 * @param {Object} params
 * @param {Object}  params.user     - Usuário autenticado (do AuthContext)
 * @param {Object}  params.ticket   - Ticket atualmente aberto
 * @param {boolean} params.espiando - Quando `true`, nenhuma marcação como lido é feita
 * @returns {{ marcarComoLida: (opcoes?: MarcarComoLidaOpcoes) => void }}
 */
const useMarkAsRead = ({ user, ticket, espiando = false }) => {
  /**
   * Ref de debounce: evita múltiplas requisições paralelas para o mesmo ticket
   * quando várias mensagens chegam
   */
  const debounceRef = useRef(null)

  /**
   * Envia uma requisição PUT para marcar o ticket como lido.
   *
   * @typedef {Object} MarcarComoLidaOpcoes
   * @property {boolean} [fromMe=false]    - Se a mensagem foi enviada pelo próprio usuário (via WhatsApp/canal)
   * @property {number}  [usuarioId=null]  - ID do usuário autor da mensagem (para conversas internas)
   * @property {boolean} [lForcado=false]   - Quando `true`, ignora todas as condições e força a marcação
   *                                         (use ao abrir/carregar a conversa pela primeira vez)
   */
  const marcarComoLida = useCallback(
    ({ fromMe = false, usuarioId = null, lForcado = false } = {}) => {
      if (!ticket?.id) return

      // Modo espiar: nunca marca como lida (o agente está apenas observando)
      if (espiando) return

      if (!lForcado) {
        // Mensagens enviadas pelo próprio canal (ex: WhatsApp) já são lidas pelo remetente
        if (fromMe) return

        // Conversas internas: não marca como lida se a mensagem foi criada pelo usuário logado
        const lEhConversaInterna = ticket.canalTipoId === canais.interno
        const lMensagemDoUsuarioLogado = usuarioId === user?.id
        if (lEhConversaInterna && lMensagemDoUsuarioLogado) return
      }

      // Debounce: cancela chamada anterior e agenda nova, garantindo que apenas
      // uma requisição seja feita após um burst de mensagens
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(async () => {
        try {
          await api.put(`/tickets/${ticket.id}`)
        } catch (error) {
          console.error("[useMarkAsRead] Erro ao marcar ticket como lido:", error)
        }
      }, 300)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticket?.id, ticket?.canalTipoId, user?.id, espiando],
  )

  /**
   * Cancela o debounce pendente ao desmontar o componente ou quando o ticket muda,
   * evitando PUT tardio para um ticket que já não está ativo.
   */
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
  }, [ticket?.id])

  return { marcarComoLida }
}

export default useMarkAsRead
